import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  or,
} from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { Rock, User, COLLECTION_NAMES } from '../types';

/**
 * Hook for Historical Rocks data with Firestore
 * Provides achieved and dropped rocks from past quarters
 */
export function useHistoricalRocks(userId: string | undefined) {
  const [rocks, setRocks] = useState<Rock[]>([]);
  const { eosUsers = [] } = useAuth() as { eosUsers: User[] };
  const [loading, setLoading] = useState(true);

  // Load historical rocks (achieved or dropped)
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    // Query for rocks that are either achieved or dropped
    // Firestore doesn't support OR queries directly, so we'll fetch all and filter
    const rocksQuery = query(
      collection(db, COLLECTION_NAMES.ROCKS),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      rocksQuery,
      (snapshot) => {
        const rocksData = snapshot.docs
          .map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              dueDate: data.dueDate?.toDate?.() || data.dueDate,
              createdAt: data.createdAt || data.createdAt,
              updatedAt: data.updatedAt || data.updatedAt,
              achievedAt: data.achievedAt?.toDate?.() || data.achievedAt,
              droppedAt: data.droppedAt?.toDate?.() || data.droppedAt,
            } as Rock;
          })
          .filter((rock) => rock.isAchieved || rock.isDropped); // Filter for historical rocks
        setRocks(rocksData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading historical rocks:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  // Filter rocks by type (all/achieved/dropped) and owner
  const getFilteredRocks = (
    filterType: 'all' | 'achieved' | 'dropped',
    ownerId: string | 'all'
  ) => {
    let filtered = rocks;

    // Filter by type
    if (filterType === 'achieved') {
      filtered = filtered.filter((rock) => rock.isAchieved);
    } else if (filterType === 'dropped') {
      filtered = filtered.filter((rock) => rock.isDropped);
    }

    // Filter by owner
    if (ownerId !== 'all') {
      filtered = filtered.filter((rock) => rock.ownerId === ownerId);
    }

    // Sort by date (achievedAt or droppedAt descending)
    return filtered.sort((a, b) => {
      const dateA = a.isAchieved
        ? (a.achievedAt instanceof Date ? a.achievedAt.getTime() : 0)
        : a.isDropped
        ? (a.droppedAt instanceof Date ? a.droppedAt.getTime() : 0)
        : 0;
      const dateB = b.isAchieved
        ? (b.achievedAt instanceof Date ? b.achievedAt.getTime() : 0)
        : b.isDropped
        ? (b.droppedAt instanceof Date ? b.droppedAt.getTime() : 0)
        : 0;
      return dateB - dateA;
    });
  };

  // Calculate statistics
  const getStats = (ownerId: string | 'all') => {
    const filtered = ownerId === 'all' 
      ? rocks 
      : rocks.filter((rock) => rock.ownerId === ownerId);
    
    return {
      total: filtered.length,
      achieved: filtered.filter((rock) => rock.isAchieved).length,
      dropped: filtered.filter((rock) => rock.isDropped).length,
    };
  };

  return {
    rocks,
    users: eosUsers,

    loading,
    getFilteredRocks,
    getStats,
  };
}

